import React, { useEffect } from 'react';
import 'react-phone-number-input/style.css';
import PhoneInput, { Country } from 'react-phone-number-input';
import styled from 'styled-components';
import { Box, FormHelperText, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import { fetchCountryCode } from '../../redux/booking/bookingSlice';

const StyledPhoneInput = styled(PhoneInput)`
  ${({ theme, error, disabled = false }) => {
    let borderColor: string;
    if (disabled) {
      borderColor = '1px solid var(--Disabled-Color, #EEE) !important';
    } else if (error) {
      borderColor = '1px solid var(--Alert-Sunset-Orange, #FF9A9A) !important';
    } else {
      borderColor = '1px solid var(--Accent-Moonstone-Blue, #7CAEBE) !important';
    }

    return `
      border: ${borderColor};
      border-radius: 10px;
      height: 47px;
      padding: 12px;
      width: 100%;
      background-color: ${disabled ? '#EEE' : theme.palette.background.paper};
      &:focus {
        outline: none;
        border: none;
      }
      .PhoneInputInput {
        border: none;
        height: 100%;
        font-size: ${theme.typography.fontSize}px; 
        color: ${theme.palette.primary.main};
        opacity: ${disabled ? 0.6 : 1};
        &::placeholder {
          color: #80808080; 
        }

        &:focus {
          outline: none;
        }
      }

      .PhoneInputCountrySelect {
        border: none;
        background-color: ${theme.palette.background.paper}; 
        font-size: medium;

        &:focus {
          background-color: ${theme.palette.background.paper}; 
        }

        &::-webkit-scrollbar {
          width: 6px;
        }
      }
    `;
  }}
`;

interface ICustomPhoneNumberInputProps {
  value?: string;
  handleChange?: (_value: string) => void;
  required?: boolean;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  name?: string;
  disabled?: boolean;
  optional?: boolean;
  countryCode?: string;
  isMiniWeb?: boolean;
  labelRequired?: boolean;
}

const CustomPhoneNumberInput = ({
  value,
  handleChange,
  required,
  onBlur,
  error,
  name,
  disabled,
  optional,
  countryCode,
  isMiniWeb = false,
  labelRequired = true,
}: ICustomPhoneNumberInputProps) => {
  const fetchedCountryCode = useAppSelector((state) => state.booking.countryCode);
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!countryCode || !fetchedCountryCode) {
      dispatch(fetchCountryCode());
    }
  }, [countryCode]);

  const getCountryCode = () => {
    if (countryCode) return countryCode;
    if (fetchedCountryCode) return fetchedCountryCode;
    return 'SG';
  };

  return (
    <Box width="100%">
      {!isMiniWeb && labelRequired && (
        <Typography variant="meta" color="primary">
          {`${t(optional ? 'addNewClient.tpOptional' : 'addNewClient.tp')}`}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      )}
      <StyledPhoneInput
        name={name}
        placeholder="XX XX XX XX XX"
        value={value}
        onChange={handleChange || (() => {})}
        defaultCountry={getCountryCode() as Country}
        theme={theme}
        onBlur={onBlur}
        error={error}
        disabled={disabled}
      />
      {error && (
        <FormHelperText sx={{ color: 'red', marginTop: '-1px' }}>{t(error)}</FormHelperText>
      )}
    </Box>
  );
};

export default CustomPhoneNumberInput;
